import LoadingButton, { LoadingButtonProps } from "@mui/lab/LoadingButton";
import {
  Button,
  ButtonProps,
  FormControlLabel,
  Radio,
  SxProps,
  Typography,
  TypographyProps,
  useRadioGroup,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import Switch from "@app/common/components/Switch";

import Stack, { Row, StackProps } from "@rui/Stack";

export const Container = styled((props: StackProps) => (
  <Stack {...props} spacing={props.spacing ?? { xxs: 2, md: 3 }} />
))(() => ({
  padding: "24px",
}));

export const Header = styled((props: TypographyProps) => (
  <Typography variant="h2" {...props} />
))(({ theme }) => ({
  color: theme.palette.shades.neutral[800],
  textAlign: "center",
  fontSize: "1.3125rem",
  fontWeight: 600,
  lineHeight: "25px",
}));

export const SubHeader = styled((props: TypographyProps) => (
  <Typography variant="bodyPrimaryReg" {...props} />
))(({ theme }) => ({
  color: theme.palette.shades.neutral[600],
  textAlign: "center",
  fontSize: "1rem",
  lineHeight: "20px",
}));

export const Disclaimer = styled((props: TypographyProps) => (
  <Typography variant="bodySecondaryReg" {...props} />
))(({ theme }) => ({
  color: theme.palette.shades.neutral[600],
  textAlign: "center",
  fontSize: "0.75rem",
}));

export const Section = styled((props: StackProps) => (
  <Stack spacing={1} {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.shades.neutral[100]}`,
  borderRadius: "8px",
  padding: "16px",
  width: "100%",
}));

export const SectionTitle = styled((props: TypographyProps) => (
  <Typography variant="bodyPrimaryMed" {...props} />
))(({ theme }) => ({
  color: theme.palette.shades.neutral[800],
  lineHeight: "20px",
  textAlign: "center",
  width: "100%",
}));

interface SettingsToggleProps {
  checked?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  children: React.ReactNode;
  sx?: SxProps;
}
export const SettingsToggle = ({
  checked,
  onChange,
  children,
}: SettingsToggleProps) => {
  return (
    <FormControlLabel
      control={<Switch checked={checked} onChange={onChange} />}
      label={
        <Typography
          variant="bodySecondaryMed"
          sx={{ color: "shades.neutral.800" }}
        >
          {children}
        </Typography>
      }
    />
  );
};

export const SettingsDescription = styled((props: TypographyProps) => (
  <Typography variant="bodySecondaryReg" {...props} />
))(({ theme }) => ({ color: theme.palette.shades.neutral[500] }));

export const Footer = styled((props: StackProps) => (
  <Stack justifyContent="center" {...props} />
))(() => ({ padding: "8px 24px", width: "100%" }));

export const SkipButton = styled((props: ButtonProps) => (
  <Button variant="text" {...props} />
))(({ theme }) => ({
  color: theme.palette.shades.neutral[500],
  fontWeight: 400,
  fontSize: "0.875rem",
  lineHeight: "18px",
  padding: "12px 24px",
}));

export const ContinueButton = styled((props: LoadingButtonProps) => (
  <LoadingButton variant="contained" {...props} />
))(() => ({
  padding: "12px 24px",
}));

export const NumberedStep = ({
  step,
  children,
}: {
  step: number;
  children: React.ReactNode;
}) => {
  return (
    <Row alignItems="flex-start" spacing={1}>
      <Stack
        variant="center"
        sx={{
          backgroundColor: "shades.ghost.500",
          borderRadius: "8px",
          width: "24px",
          height: "24px",
          flexShrink: 0,
        }}
      >
        <Typography
          variant="bodySecondaryMed"
          sx={{ color: "shades.primary.500" }}
        >
          {step}
        </Typography>
      </Stack>
      <Row variant="left" sx={{ minHeight: "24px" }}>
        <Typography
          variant="bodySecondaryReg"
          sx={{ color: "shades.neutral.600" }}
        >
          {children}
        </Typography>
      </Row>
    </Row>
  );
};

export const MeetingOption = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => {
  const groupState = useRadioGroup();
  const selectedValue = groupState?.value;

  return (
    <FormControlLabel
      control={<Radio />}
      value={value}
      sx={{
        marginRight: 0,
        padding: "6px 0",
        borderRadius: "8px",
        border: (theme) =>
          `2px solid ${
            value === selectedValue
              ? theme.palette.shades.primary[500]
              : theme.palette.shades.neutral[100]
          }`,
        marginBottom: "8px",
      }}
      label={
        <Typography
          variant="h3"
          sx={{
            fontWeight: 500,
            fontSize: "1.125rem",
            color:
              value === selectedValue
                ? "shades.neutral.700"
                : "shades.neutral.500",
          }}
        >
          {label}
        </Typography>
      }
    />
  );
};

export const WaitForHostModule = styled(Section)({
  backgroundColor: "#fafafa",
  alignItems: "center",
});
